import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createProductionOrder, updateProductionOrder, getProductionOrder, type ProductionOrderListItem, type SaveProductionOrderPayload,
} from '../../api/productieOrdine'
import { listArticles } from '../../api/nomenclatoare'
import { listWarehouses } from '../../api/depozite'
import { listEquipment, listMolds } from '../../api/echipamente'
import { listOrders } from '../../api/comenzi'
import { listUsers } from '../../api/admin'
import { errorMessage } from '../../api/client'

const schema = z.object({
  productArticleId: z.string().min(1, 'Selectați produsul'),
  warehouseId: z.string().min(1, 'Selectați depozitul'),
  plannedQuantityThousands: z.string().min(1, 'Câmp obligatoriu'),
  plannedStartDate: z.string().min(1, 'Câmp obligatoriu'),
  plannedEndDate: z.string().min(1, 'Câmp obligatoriu'),
  equipmentId: z.string().min(1, 'Selectați utilajul'),
  moldId: z.string().nullable(),
  shiftNumber: z.string().optional(),
  operatorUserId: z.string().nullable(),
  sourceOrderId: z.string().nullable(),
  priority: z.enum(['Scazuta', 'Normala', 'Ridicata', 'Urgenta']),
  notes: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  productArticleId: '', warehouseId: '', plannedQuantityThousands: '', plannedStartDate: new Date().toISOString().slice(0, 10),
  plannedEndDate: new Date().toISOString().slice(0, 10), equipmentId: '', moldId: null, shiftNumber: '', operatorUserId: null,
  sourceOrderId: null, priority: 'Normala', notes: '',
}

export default function OrdineFormDialog({ target, onClose }: { target: ProductionOrderListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: warehouses } = useQuery({ queryKey: ['stocuri', 'depozite'], queryFn: listWarehouses, enabled: open })
  const { data: equipment } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'utilaje'], queryFn: listEquipment, enabled: open })
  const { data: molds } = useQuery({ queryKey: ['mentenanta', 'echipamente', 'matrite'], queryFn: listMolds, enabled: open })
  const { data: orders } = useQuery({ queryKey: ['comercial', 'comenzi'], queryFn: listOrders, enabled: open })
  const { data: users } = useQuery({ queryKey: ['admin', 'users'], queryFn: () => listUsers(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['productie', 'ordine', editing ? (target as ProductionOrderListItem).id : null],
    queryFn: () => getProductionOrder((target as ProductionOrderListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing && detail) {
      reset({
        productArticleId: detail.productArticleId, warehouseId: detail.warehouseId,
        plannedQuantityThousands: String(detail.plannedQuantityThousands),
        plannedStartDate: detail.plannedStartDate, plannedEndDate: detail.plannedEndDate,
        equipmentId: detail.equipmentId, moldId: detail.moldId, shiftNumber: detail.shiftNumber != null ? String(detail.shiftNumber) : '',
        operatorUserId: detail.operatorUserId, sourceOrderId: detail.sourceOrderId, priority: detail.priority, notes: detail.notes ?? '',
      })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveProductionOrderPayload = {
        productArticleId: values.productArticleId, warehouseId: values.warehouseId,
        plannedQuantityThousands: Number(values.plannedQuantityThousands),
        plannedStartDate: values.plannedStartDate, plannedEndDate: values.plannedEndDate,
        equipmentId: values.equipmentId, moldId: values.moldId || null,
        shiftNumber: values.shiftNumber ? Number(values.shiftNumber) : null,
        operatorUserId: values.operatorUserId || null, sourceOrderId: values.sourceOrderId || null,
        priority: values.priority, notes: values.notes || null,
      }
      if (editing) return updateProductionOrder((target as ProductionOrderListItem).id, payload)
      return createProductionOrder(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productie', 'ordine'] })
      onClose()
    },
  })

  const readOnly = editing && detail && detail.status !== 'Planificat'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? `Ordin ${detail?.number}` : 'Ordin de producție nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {readOnly && <Alert severity="info">Doar ordinele planificate pot fi editate.</Alert>}
            <Controller name="productArticleId" control={control} render={({ field }) => (
              <Autocomplete
                disabled={!!readOnly} options={articles?.filter((a) => a.type !== 'Set').map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Produs finit" error={!!errors.productArticleId} helperText={errors.productArticleId?.message} />}
              />
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="warehouseId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} disabled={!!readOnly} options={warehouses?.map((w) => w.id) ?? []}
                  getOptionLabel={(id) => warehouses?.find((w) => w.id === id)?.name ?? id}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Depozit" error={!!errors.warehouseId} helperText={errors.warehouseId?.message} />}
                />
              )} />
              <Controller name="plannedQuantityThousands" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="number" label="Cantitate (mii buc)" sx={{ width: 180 }} error={!!errors.plannedQuantityThousands}
                  helperText={errors.plannedQuantityThousands?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="plannedStartDate" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="date" label="Planificat start" sx={{ flexGrow: 1 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.plannedStartDate} helperText={errors.plannedStartDate?.message} />
              )} />
              <Controller name="plannedEndDate" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} type="date" label="Planificat final" sx={{ flexGrow: 1 }} slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.plannedEndDate} helperText={errors.plannedEndDate?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="equipmentId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} disabled={!!readOnly} options={equipment?.map((e) => e.id) ?? []}
                  getOptionLabel={(id) => { const e = equipment?.find((x) => x.id === id); return e ? `${e.code} — ${e.name}` : id }}
                  value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                  renderInput={(params) => <TextField {...params} label="Utilaj" error={!!errors.equipmentId} helperText={errors.equipmentId?.message} />}
                />
              )} />
              <Controller name="moldId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} disabled={!!readOnly} options={molds?.map((m) => m.id) ?? []}
                  getOptionLabel={(id) => { const m = molds?.find((x) => x.id === id); return m ? `${m.code} — ${m.name}` : id }}
                  value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                  renderInput={(params) => <TextField {...params} label="Matriță (opțional)" />}
                />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="shiftNumber" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} select label="Schimb (opțional)" sx={{ width: 160 }}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="1">Schimb 1</MenuItem>
                  <MenuItem value="2">Schimb 2</MenuItem>
                  <MenuItem value="3">Schimb 3</MenuItem>
                </TextField>
              )} />
              <Controller name="operatorUserId" control={control} render={({ field }) => (
                <Autocomplete
                  sx={{ flexGrow: 1 }} disabled={!!readOnly} options={users?.map((u) => u.id) ?? []}
                  getOptionLabel={(id) => { const u = users?.find((x) => x.id === id); return u ? `${u.firstName} ${u.lastName}` : id }}
                  value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                  renderInput={(params) => <TextField {...params} label="Operator (opțional)" />}
                />
              )} />
              <Controller name="priority" control={control} render={({ field }) => (
                <TextField {...field} disabled={!!readOnly} select label="Prioritate" sx={{ width: 150 }} error={!!errors.priority} helperText={errors.priority?.message}>
                  <MenuItem value="Scazuta">Scăzută</MenuItem>
                  <MenuItem value="Normala">Normală</MenuItem>
                  <MenuItem value="Ridicata">Ridicată</MenuItem>
                  <MenuItem value="Urgenta">Urgentă</MenuItem>
                </TextField>
              )} />
            </Stack>
            <Controller name="sourceOrderId" control={control} render={({ field }) => (
              <Autocomplete
                disabled={!!readOnly} options={orders?.map((o) => o.id) ?? []}
                getOptionLabel={(id) => { const o = orders?.find((x) => x.id === id); return o ? `${o.orderNumber} — ${o.clientName}` : id }}
                value={field.value} onChange={(_, v) => field.onChange(v ?? null)}
                renderInput={(params) => <TextField {...params} label="Comandă sursă (opțional — make-to-order)" />}
              />
            )} />
            <Controller name="notes" control={control} render={({ field }) => (
              <TextField {...field} disabled={!!readOnly} label="Note" fullWidth multiline minRows={2} />
            )} />

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          {!readOnly && (
            <Button type="submit" variant="contained" disabled={mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
              Salvează
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  )
}
